import React, { useCallback, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Dropdown, useDropdownState, useBreakpoint, csx } from '@vtex/admin-ui'
import { useHistory } from 'react-router'
import { isEmpty } from 'ramda'

import { useOrderForm } from '../../../../core/requests/checkout'
import {
  Step,
  StepTitle,
  CartSummary,
  CheckoutButtonMobile,
} from '../../components'
import { ShippingPackage, ContinueButton } from './components'
import {
  getDeliveryMethodRouteByPackages,
  isClientProfileComplete,
} from '../../utils'
import { useUpdateFormListener } from '../../../../checkout-instore-bridge/hooks/useUpdateFormListener'
import { useCheckout } from '../../state'
import { ShippingMethod } from '../../constants/shipping'
import { useShippingMethodSelection } from './hooks'
import { TopbarMenuArea } from '../../../Instore/components/Header/Topbar'
import { BackButton } from '../../../../core/components'
import { CheckoutRoute } from '../../constants'
import { isClientProfileDataWithSalesAssociateEmail } from '../../../../core/utils/orderForm'
import { useFeatureFlag } from '../../../../core/hooks'
import { getNextAddressRoute } from '../../utils/getNextAddressRoute'

export const ShippingMethodSelectionScreen = () => {
  const { formatMessage } = useIntl()

  const { breakpoint } = useBreakpoint()
  const isMobileOrTablet = ['mobile', 'tablet'].includes(breakpoint)
  const { checkout } = useCheckout()
  const history = useHistory()
  const { isShippingDropdownEnabled } = useFeatureFlag()
  const [nextAddressStep] = getNextAddressRoute(checkout.clientData.address)
  const getNextAddressStep = () => {
    return nextAddressStep === undefined || nextAddressStep?.name === 'street'
      ? CheckoutRoute.HomeDeliveryAddressSteps
      : `${CheckoutRoute.HomeDeliveryAddressSteps}/${nextAddressStep.route}`
  }

  const {
    updateLogisticsInfoToTakeAway,
    updateLogisticsInfoToCheapest,
    updateLogisticsInfoToFatest,
    updateLogisticsInfoToPickup,
    updateLogisticsInfoToDefaultSelectedSla,
    packages,
    loading,
    getAvailableShippingMethods,
  } = useShippingMethodSelection()

  const SHIPPING_METHODS = getAvailableShippingMethods()

  const [INITIAL_SHIPPING_METHOD] = SHIPPING_METHODS

  const shippingActionHash = {
    [ShippingMethod.Fastest]: updateLogisticsInfoToFatest,
    [ShippingMethod.Cheapest]: updateLogisticsInfoToCheapest,
    [ShippingMethod.PickUp]: updateLogisticsInfoToPickup,
    [ShippingMethod.TakeAway]: updateLogisticsInfoToTakeAway,
    [ShippingMethod.Customize]: updateLogisticsInfoToDefaultSelectedSla,
  }

  const { orderForm } = useOrderForm({
    swrConfig: {
      // Force revalidateOnMount false to avoid orderform request concurrency with the useEffect below
      revalidateOnMount: false,
    },
  })

  const isClientProfileCompleted =
    isClientProfileComplete(orderForm?.clientProfileData) &&
    !isClientProfileDataWithSalesAssociateEmail(orderForm)

  const handleShippingChange = (
    selectedItem: ShippingMethod | null | undefined
  ) => {
    if (!selectedItem) {
      return
    }

    const shippingAction = shippingActionHash[selectedItem]

    shippingAction()
  }

  const dropdownState = useDropdownState({
    items: SHIPPING_METHODS,
    initialSelectedItem: INITIAL_SHIPPING_METHOD,
    onSelectedItemChange: (event) => handleShippingChange(event.selectedItem),
  })

  const validateEmail = (email?: string | null) =>
    email && !email.includes('-anonymous@vtex.com')

  const getNextIdentificationStep = useCallback(() => {
    if (isClientProfileDataWithSalesAssociateEmail(orderForm)) {
      return 'identify'
    }

    if (orderForm?.clientProfileData?.document) {
      return 'email'
    }

    if (validateEmail(orderForm?.clientProfileData?.email)) {
      return 'document'
    }

    return 'identify'
  }, [orderForm])

  const handleContinueClick = () => {
    const nextRoute = getDeliveryMethodRouteByPackages(packages)

    const nextIdentificationStep = getNextIdentificationStep()

    const finalRoute = isClientProfileCompleted
      ? CheckoutRoute.OrderSummary
      : `${nextRoute}/${nextIdentificationStep}`

    const shippingRoute =
      orderForm?.shippingData.address?.number === '' &&
      nextRoute === CheckoutRoute.HomeDelivery
        ? getNextAddressStep()
        : `${nextRoute}/confirm-address`

    history.push(
      isClientProfileCompleted && nextRoute === CheckoutRoute.HomeDelivery
        ? shippingRoute
        : finalRoute
    )
  }

  const handleItemSlaChange = () => {
    dropdownState.selectItem(ShippingMethod.Customize)
  }

  const hasSlas = orderForm?.shippingData.logisticsInfo.every(
    (logisticsInfoItem) =>
      !!logisticsInfoItem.slas && !isEmpty(logisticsInfoItem.slas)
  )

  const continueButtonProps = {
    onClick: handleContinueClick,
    loading,
    disabled: loading || !hasSlas,
  }

  useEffect(() => {
    handleShippingChange(dropdownState.selectedItem)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useUpdateFormListener()

  return (
    <>
      <TopbarMenuArea>
        <BackButton />
      </TopbarMenuArea>
      <div>
        <Step data-testid="shipping-method-step">
          <div
            className={csx({
              marginBottom: '$space-4',
            })}
          >
            <StepTitle>
              <FormattedMessage id="checkout.shipping.select-shipping-method" />
            </StepTitle>
          </div>
          {isShippingDropdownEnabled() && (
            <div
              className={csx({
                marginBottom: '$space-6',
              })}
            >
              <Dropdown
                label="Shipping Method Selector"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore: According to the admin-ui documentation, the variants should be solid, soft or text,
                // but the typings are pointing to other variants.
                variant="soft"
                state={dropdownState}
                items={SHIPPING_METHODS}
                renderItem={(item) =>
                  formatMessage({ id: item ? item?.toString() : ' ' })
                }
                data-testid="shipping-method-selection-dropdown"
              />
            </div>
          )}
          {packages.map((pkg, index) => (
            <ShippingPackage
              key={`${pkg.deliveryChannel}-${index}`}
              data-testid="shipping-package"
              currentPackage={pkg}
              packageNumber={index + 1}
              onItemSlaChange={handleItemSlaChange}
              selectedShippingMethod={
                dropdownState.selectedItem as ShippingMethod
              }
            />
          ))}
        </Step>
      </div>
      {isMobileOrTablet && (
        <div
          className={csx({
            paddingTop: '$space-3',
            background: '$gray05',
          })}
        />
      )}
      <CartSummary>
        {isMobileOrTablet ? (
          <CheckoutButtonMobile {...continueButtonProps} />
        ) : (
          <ContinueButton {...continueButtonProps} />
        )}
      </CartSummary>
    </>
  )
}
